import { injectable } from 'inversify';

import { t } from '@vk-hr-tek/core/translations';

import { WidgetPartitionView } from '@app/gen/dashboard';
import { modules } from '@app/modules';
import { ManageableCompanyItem } from '@app/gen/users';

import { PartitionItem } from '../types';

@injectable()
export class PartitionMapper {
  private processEmployeePartition(
    partition: WidgetPartitionView,
  ): PartitionItem | null {
    switch (partition.type) {
      case 'absences':
        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsEmployee.absences'),
          href: `employee/${modules.absences.basename}`,
          icon: 'vkhrtek_absence_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Надо оформить',
              count: partition.count_my,
              href: `employee/${modules.absences.basename}`,
              type: 'mine',
            },
            {
              text: {
                withCount: 'На оформлении',
                empty: 'Посмотреть все',
              },
              count: partition.count_all,
              href: `employee/${modules.absences.basename}?e_filter_actor=2`,
              type: 'all',
            },
          ],
          ...(partition?.links_visibility?.[1] && {
            extra: {
              type: 'add',
              href: `employee/${modules.absences.basename}?create_absences=true`,
            },
          }),
        };

      case 'personal_profile':
        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsEmployee.personal'),
          href: `/${modules.personal.basename}`,
          icon: 'vkhrtek_personal_3d',
          size: partition.width || 1,
          links: [
            {
              text: t('dashboard.widgets.partitionsEmployee.personalLinkText'),
              href: `/${modules.personal.basename}`,
              type: 'mine',
            },
          ],
          extra: {
            type: 'go',
            href: `/${modules.personal.basename}`,
          },
        };

      case 'payslips':
        return {
          alwaysColoredText: false,
          type: partition.type,
          title: t('dashboard.widgets.partitionsEmployee.payslips'),
          href: `/employee/${modules.payslips.basename}`,
          icon: 'vkhrtek_payslips_3d',
          size: partition.width || 1,
          links: [
            {
              text: t('dashboard.widgets.partitionsEmployee.payslipsLinkText'),
              href: `/employee/${modules.payslips.basename}`,
              type: 'mine',
            },
            ...(partition?.links_visibility?.[1]
              ? [
                  {
                    text: 'Заказать справку',
                    href: `/employee/${modules.events.basename}?create_event=true`,
                    type: 'mine' as const,
                  },
                ]
              : []),
          ],
          extra: {
            type: 'go',
            href: `/employee/${modules.payslips.basename}`,
          },
        };

      case 'policies':
        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsEmployee.policy'),
          href: `employee/${modules.policy.basename}`,
          icon: 'vkhrtek_policy_3d',
          size: partition.width || 1,
          links: [
            {
              text: {
                withCount: 'Необходимо ознакомиться',
                empty: 'Посмотреть все',
              },
              count: partition.count_my,
              href: `employee/${modules.policy.basename}`,
              type: 'mine',
            },
          ],
          extra: {
            type: 'go',
            href: `employee/${modules.policy.basename}`,
          },
        };

      case 'review':
        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsEmployee.review'),
          href: `employee/${modules.review.basename}`,
          icon: 'vkhrtek_review_3d',
          size: partition.width || 1,
          links: [
            {
              text: {
                withCount: 'Заполнить цели',
                empty: t('dashboard.widgets.partitionsEmployee.reviewLinkText'),
              },
              count: partition.count_my,
              href: `employee/${modules.review.basename}`,
              type: 'mine',
            },
          ],
          extra: {
            type: 'go',
            href: `employee/${modules.review.basename}`,
          },
        };

      case 'competencies':
        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsEmployee.competencies'),
          href: `employee/${modules.competencies.basename}`,
          icon: 'vkhrtek_competencies_3d',
          size: partition.width || 1,
          links: [
            {
              text: {
                withCount: 'Заполнить анкету',
                empty: 'Посмотреть профиль компетенций',
              },
              count: partition.count_my,
              href: `employee/${modules.competencies.basename}`,
              type: 'mine',
            },
          ],
          extra: {
            type: 'go',
            href: `employee/${modules.competencies.basename}`,
          },
        };

      case 'training':
        return {
          type: partition.type,
          disabled: true,
          title: t('dashboard.widgets.partitionsEmployee.training'),
          href: '',
          icon: 'vkhrtek_training_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Новый сервис. Пока недоступен',
            },
          ],
        };

      case 'benefits':
        return {
          type: partition.type,
          disabled: true,
          title: t('dashboard.widgets.partitionsEmployee.benefits'),
          href: '',
          icon: 'vkhrtek_benefits_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Новый сервис. Пока недоступен',
            },
          ],
        };

      case 'statements':
        return {
          type: partition.type,
          disabled: true,
          title: t('dashboard.widgets.partitionsEmployee.statements'),
          href: '',
          icon: 'vkhrtek_statements_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Новый сервис. Пока недоступен',
            },
          ],
        };

      case 'businesstrip':
        return {
          type: partition.type,
          disabled: true,
          title: t('dashboard.widgets.partitionsEmployee.businesstrip'),
          href: '',
          icon: 'vkhrtek_businesstrip_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Новый сервис. Пока недоступен',
            },
          ],
        };

      default:
        return null;
    }
  }

  private processCompanyPartition(
    partition: WidgetPartitionView,
    companies: ManageableCompanyItem[],
  ): PartitionItem | null {
    switch (partition.type) {
      case 'absences':
        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsCompany.absences'),
          href: `/company/${modules.absences.basename}`,
          icon: 'vkhrtek_absence_3d',
          size: partition.width || 1,
          links: [
            {
              text: {
                empty: 'Посмотреть календарь',
                withCount: 'Ждут моего согласования',
              },
              count: partition.count_my,
              href: `/company/${modules.absences.basename}`,
              type: 'mine',
            },
          ],
          extra: {
            type: 'go',
            href: `/company/${modules.absences.basename}`,
          },
        };

      case 'benefits':
        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsCompany.benefits'),
          disabled: true,
          href: '',
          icon: 'vkhrtek_benefits_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Новый сервис. Пока недоступен',
            },
          ],
        };

      case 'candidates':
        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsCompany.candidates'),
          href: `/company/${modules.candidates.basename}`,
          icon: 'vkhrtek_candidates_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Надо согласовать',
              count: partition.count_my,
              href: `/company/${modules.candidates.basename}`,
              type: 'mine',
            },
            {
              text: {
                empty: 'Посмотреть все анкеты',
                withCount: 'Всего в работе',
              },
              count: partition.count_all,
              href: {
                empty: `/company/${modules.candidates.basename}?filter_actor=2`,
                withCount: `/company/${modules.candidates.basename}?filter_actor=2`,
              },
              type: 'all',
            },
          ],
          extra: {
            type: 'add',
            href: `/company/${modules.candidates.basename}?create_event=true`,
          },
        };

      case 'competencies':
        return {
          type: partition.type,
          disabled: true,
          title: t('dashboard.widgets.partitionsCompany.competencies'),
          href: '',
          icon: 'vkhrtek_competencies_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Новый сервис. Пока недоступен',
            },
          ],
        };

      case 'events':
        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsCompany.events'),
          href: `/company/${modules.events.basename}`,
          icon: 'vkhrtek_event_3d',
          size: partition.width || 1,
          links: [
            {
              text: t('dashboard.widgets.partitionsCompany.eventsLinksText'),
              count: partition.count_my,
              href: {
                withCount: `/company/${modules.events.basename}`,
                empty: `/company/${modules.events.basename}?filter_actor=2`,
              },
              type: 'mine',
            },
          ],
          extra: {
            type: 'add',
            href: `/company/${modules.events.basename}?create_event=true`,
          },
        };

      case 'organization': {
        const manageableCompany = companies.find((company) =>
          company.functional_manageable_units?.some(
            (unit) => unit.role_in_unit === 'manager',
          ),
        );
        const companyId = manageableCompany?.id;
        const unitId = manageableCompany?.functional_manageable_units?.find(
          (unit) => unit.role_in_unit === 'manager',
        )?.unit?.id;

        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsCompany.organization'),
          href: `/${modules.structure.basename}`,
          icon: 'vkhrtek_structure_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Мои подчиненные',
              href: `/organization${companyId ? `?companyId=${companyId}` : ''}${unitId ? `&unitId=${unitId}` : ''}`,
              type: 'mine',
            },
          ],
          extra: {
            type: 'go',
            href: '/organization',
          },
        };
      }

      case 'payroll':
        return {
          type: partition.type,
          disabled: true,
          title: t('dashboard.widgets.partitionsCompany.payroll'),
          href: '',
          icon: 'vkhrtek_payroll_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Новый сервис. Пока недоступен',
            },
          ],
        };

      case 'review':
        return {
          type: partition.type,
          title: t('dashboard.widgets.partitionsCompany.review'),
          href: `/company/${modules.review.basename}`,
          icon: 'vkhrtek_review_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Надо согласовать',
              count: partition.count_my,
              href: `/company/${modules.review.basename}`,
              type: 'mine',
            },
            {
              text: {
                empty: t('dashboard.widgets.partitionsCompany.reviewLinkText'),
                withCount: 'Всего в работе',
              },
              count: partition.count_all,
              href: `/company/${modules.review.basename}?filter_actor=2`,
              type: 'all',
            },
          ],
          extra: {
            type: 'go',
            href: `/company/${modules.review.basename}`,
          },
        };

      case 'training':
        return {
          type: partition.type,
          disabled: true,
          title: t('dashboard.widgets.partitionsCompany.training'),
          href: ``,
          icon: 'vkhrtek_training_3d',
          size: partition.width || 1,
          links: [
            {
              text: 'Новый сервис. Пока недоступен',
            },
          ],
        };

      default:
        return null;
    }
  }

  processEmployeePartitions(partitions: WidgetPartitionView[]) {
    return partitions
      .map((item) => this.processEmployeePartition(item))
      .filter((partition) => !!partition)
      .map((partition, index) => ({ ...partition, id: index }));
  }

  processCompanyPartitions(
    partitions: WidgetPartitionView[],
    companies: ManageableCompanyItem[],
  ) {
    return partitions
      .map((item) => this.processCompanyPartition(item, companies))
      .filter((partition) => !!partition)
      .map((partition, index) => ({ ...partition, id: index }));
  }

  processDesktopPagination(list: PartitionItem[], cols: number, rows: number) {
    const pages: PartitionItem[][] = [];
    const MAX_COUNT = cols * rows; // Максимальное количество ячеек на одной странице.
    let page: PartitionItem[] = []; // Последняя активная страница для наполнения.
    let sum = 0; // Количество уже использованных ячеек на определенной странице.
    let used = 0; // Количество уже использованных ячеек в строке.

    list.forEach((item) => {
      // Если элемент не помещается в текущую строку, увеличиваем sum на количество пустых ячеек и переходим на новую строку
      if (used + item.size > cols) {
        sum += cols - used;
        used = 0;
      }

      if (sum >= MAX_COUNT) {
        pages.push(page);
        page = [];
        sum = 0;
        used = 0;
      }

      page.push(item);
      sum += item.size;
      used += item.size;
    });

    if (page.length > 0) {
      pages.push(page);
    }

    return pages;
  }
}
